import { z } from 'zod';

/**
 * Trust & safety and legal (CLAUDE.md §6.7).
 *
 * Everything here is DATA, for the same reason templates are data in §5: the
 * prohibited-goods list, the report reasons and the legal document versions all
 * have to be readable by the marketing site, the admin, the API and the ops bot
 * at once. A policy that lives in one app's copy deck is a policy the other
 * three cannot enforce.
 *
 * §6.7 says this is enforced **from pilot**, not at public launch. Ten shops we
 * recruited by hand is exactly when a prohibited-goods rule is cheap to apply;
 * the first time it is expensive is the first time we need it.
 */

/* ------------------------------------------------------------------ */
/* Prohibited goods                                                    */
/* ------------------------------------------------------------------ */

/**
 * What a shop may not sell.
 *
 * The three §6.7 names outright — counterfeits, medicines, anything
 * gambling-adjacent — plus the categories that a Cambodian fashion and
 * cosmetics marketplace actually collides with. Each carries examples because
 * "prohibited: cosmetics" would be useless to a shop that sells cosmetics; the
 * line is skin-whitening injectables, not lipstick.
 */
export interface ProhibitedCategory {
  id: string;
  title: { km: string; en: string };
  detail: { km: string; en: string };
  /** Concrete cases, so a merchant can tell which side of the line they are on. */
  examples: { km: string; en: string };
}

export const PROHIBITED_CATEGORIES: readonly ProhibitedCategory[] = [
  {
    id: 'counterfeit',
    title: { km: 'ទំនិញក្លែងក្លាយ', en: 'Counterfeit goods' },
    detail: {
      km: 'ទំនិញដែលប្រើឡូហ្គោ ឬយីហោរបស់អ្នកដទៃដោយគ្មានការអនុញ្ញាត។',
      en: 'Anything carrying a brand or logo you are not authorised to sell.',
    },
    examples: {
      km: 'កាបូប នាឡិកា ឬគ្រឿងសម្អាង «copy» «AAA» «super fake»។ ការលក់ទំនិញយីហោពិតដែលអ្នកទិញមកវិញ គឺមិនអីទេ។',
      en: 'Bags, watches or cosmetics sold as "copy", "AAA" or "super fake". Reselling genuine branded stock you bought is fine.',
    },
  },
  {
    id: 'medicine',
    title: { km: 'ឱសថ និងផលិតផលព្យាបាល', en: 'Medicines and treatments' },
    detail: {
      km: 'ឱសថ ថ្នាំចាក់ ថ្នាំបំប៉ន ឬឧបករណ៍វេជ្ជសាស្ត្រដែលត្រូវការវេជ្ជបញ្ជា ឬការអនុញ្ញាត។',
      en: 'Prescription or licence-controlled drugs, injectables, supplements and medical devices.',
    },
    examples: {
      km: 'ថ្នាំចាក់សម្រាប់ធ្វើឲ្យស្បែកស ថ្នាំសម្រកទម្ងន់ ថ្នាំអង់ទីប៊ីយ៉ូទិក។ គ្រឿងសម្អាងធម្មតា ដូចជាក្រែម ឬសាប៊ូ គឺមិនអីទេ។',
      en: 'Skin-whitening injectables, weight-loss pills, antibiotics. Ordinary cosmetics such as creams and cleansers are fine.',
    },
  },
  {
    id: 'gambling',
    title: { km: 'ល្បែងស៊ីសង', en: 'Gambling and anything adjacent' },
    detail: {
      km: 'ការភ្នាល់ ឆ្នោត កាស៊ីណូ ការលក់ពិន្ទុ ឬឥណទានសម្រាប់ល្បែង។',
      en: 'Betting, lotteries, casino play, and selling credits or points used for gambling.',
    },
    examples: {
      km: 'លក់ឥណទានសម្រាប់គេហទំព័រភ្នាល់ លក់សំបុត្រឆ្នោត ទទួលភ្នាល់បាល់ទាត់។',
      en: 'Selling credit for betting sites, lottery tickets, taking football bets.',
    },
  },
  {
    id: 'weapons',
    title: { km: 'អាវុធ', en: 'Weapons' },
    detail: {
      km: 'អាវុធ គ្រាប់រំសេវ គ្រឿងផ្ទុះ និងគ្រឿងបន្លាស់របស់វា។',
      en: 'Firearms, ammunition, explosives and their components.',
    },
    examples: {
      km: 'កាំភ្លើង កាំបិតវាយប្រហារ ស្ព្រាយម្រេច។',
      en: 'Guns, fighting knives, pepper spray.',
    },
  },
  {
    id: 'drugs_alcohol_tobacco',
    title: { km: 'គ្រឿងញៀន គ្រឿងស្រវឹង និងថ្នាំជក់', en: 'Drugs, alcohol and tobacco' },
    detail: {
      km: 'គ្រឿងញៀនគ្រប់ប្រភេទ។ គ្រឿងស្រវឹង ថ្នាំជក់ និងបារីអេឡិចត្រូនិក ត្រូវការអាជ្ញាបណ្ណ ហើយមិនអាចលក់នៅទីនេះទេ។',
      en: 'Controlled substances of any kind. Alcohol, tobacco and vapes are licence-controlled and cannot be sold here.',
    },
    examples: {
      km: 'គ្រឿងញៀន បារីអេឡិចត្រូនិក ស្រាបៀរ។',
      en: 'Narcotics, vapes, beer.',
    },
  },
  {
    id: 'wildlife',
    title: { km: 'សត្វព្រៃ និងផលិតផលពីសត្វព្រៃ', en: 'Wildlife and wildlife products' },
    detail: {
      km: 'សត្វការពារ និងគ្រប់ផលិតផលដែលធ្វើពីវា។',
      en: 'Protected species and anything made from them.',
    },
    examples: {
      km: 'ភ្លុកដំរី ស្បែកខ្លា ស្នែងរមាស។',
      en: 'Ivory, tiger skin, rhino horn.',
    },
  },
  {
    id: 'adult',
    title: { km: 'ទំនិញមនុស្សពេញវ័យ', en: 'Adult content and services' },
    detail: {
      km: 'រូបភាព វីដេអូ ឬសេវាកម្មផ្លូវភេទ។',
      en: 'Sexual imagery, video or services.',
    },
    examples: { km: 'សេវាកម្មរួមភេទ រូបភាពអាសអាភាស។', en: 'Escort services, pornography.' },
  },
  {
    id: 'personal_data',
    title: { km: 'ទិន្នន័យ និងគណនីរបស់អ្នកដទៃ', en: "Other people's data and accounts" },
    detail: {
      km: 'បញ្ជីលេខទូរស័ព្ទ គណនីដែលលួច ឬឯកសារក្លែងក្លាយ។',
      en: 'Phone-number lists, stolen accounts, forged documents.',
    },
    examples: {
      km: 'លក់បញ្ជីអតិថិជន គណនីហ្វេសប៊ុក អត្តសញ្ញាណប័ណ្ណក្លែងក្លាយ។',
      en: 'Customer lists, Facebook accounts, fake IDs.',
    },
  },
  {
    id: 'stolen',
    title: { km: 'ទំនិញលួច', en: 'Stolen goods' },
    detail: {
      km: 'ទំនិញដែលអ្នកមិនមានសិទ្ធិលក់ស្របច្បាប់។',
      en: 'Anything you do not lawfully have the right to sell.',
    },
    examples: { km: 'ទូរស័ព្ទលួច ម៉ូតូគ្មានឯកសារ។', en: 'Stolen phones, motorbikes with no papers.' },
  },
];

export const prohibitedCategoryIds = PROHIBITED_CATEGORIES.map((entry) => entry.id);

/* ------------------------------------------------------------------ */
/* Legal documents                                                     */
/* ------------------------------------------------------------------ */

/**
 * Versioned so acceptance means something.
 *
 * A merchant who accepted v1 has not accepted v2, and we need to be able to
 * say which. Bump the version whenever the substance changes — not for typos.
 */
export const POLICY_DOCUMENTS = ['tos', 'privacy', 'prohibited_goods'] as const;
export type PolicyDocument = (typeof POLICY_DOCUMENTS)[number];
export const policyDocumentSchema = z.enum(POLICY_DOCUMENTS);

export const POLICY_VERSIONS: Record<PolicyDocument, string> = {
  tos: '2026-08-16',
  privacy: '2026-08-16',
  prohibited_goods: '2026-08-16',
};

/** Everything a merchant must accept before their storefront goes live. */
export const REQUIRED_ACCEPTANCES: readonly PolicyDocument[] = POLICY_DOCUMENTS;

export const acceptPolicySchema = z.object({
  documents: z.array(policyDocumentSchema).min(1),
});
export type AcceptPolicyInput = z.infer<typeof acceptPolicySchema>;

export const policyStatusSchema = z.object({
  accepted: z.array(
    z.object({
      document: policyDocumentSchema,
      version: z.string(),
      acceptedAt: z.string().datetime(),
      /** True when a newer version has been published since. */
      outdated: z.boolean(),
    }),
  ),
  /** Documents not yet accepted at their current version. */
  outstanding: z.array(policyDocumentSchema),
  versions: z.record(z.string()),
});
export type PolicyStatusDto = z.infer<typeof policyStatusSchema>;

/* ------------------------------------------------------------------ */
/* Reports                                                             */
/* ------------------------------------------------------------------ */

/**
 * Why somebody is reporting a shop.
 *
 * The prohibited-goods categories are the first reasons, because a report is
 * usually somebody pointing at one of them. `not_delivered` and `misleading`
 * are not policy breaches on their own — they are the ones a customer actually
 * has, and a report mechanism that only accepts the reasons we care about is a
 * report mechanism nobody uses.
 */
export const REPORT_REASONS = [
  ...prohibitedCategoryIds,
  'not_delivered',
  'misleading',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const reportReasonSchema = z.enum(REPORT_REASONS as unknown as [string, ...string[]]);

export const REPORT_STATUSES = ['open', 'reviewing', 'upheld', 'dismissed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const submitReportSchema = z.object({
  shopSlug: z.string().min(1).max(80),
  productId: z.string().uuid().nullable().optional(),
  reason: reportReasonSchema,
  detail: z.string().max(2000).optional().default(''),
  /**
   * Optional and deliberately not required: §6.7 wants a report mechanism, and
   * demanding a contact detail from somebody reporting counterfeit goods is how
   * you get no reports.
   */
  contact: z.string().max(120).optional(),
  /** Honeypot — see the lead form. Bots fill it; humans never see it. */
  company: z.string().max(200).optional(),
});
export type SubmitReportInput = z.infer<typeof submitReportSchema>;

export const reportSchema = z.object({
  id: z.string().uuid(),
  shopId: z.string().uuid(),
  shopName: z.string(),
  shopSlug: z.string(),
  productId: z.string().uuid().nullable(),
  reason: reportReasonSchema,
  detail: z.string(),
  contact: z.string().nullable(),
  status: z.enum(REPORT_STATUSES),
  resolution: z.string().nullable(),
  createdAt: z.string().datetime(),
  reviewedAt: z.string().datetime().nullable(),
});
export type ReportDto = z.infer<typeof reportSchema>;

/** Human-readable reason labels, shared by the report form and the ops bot. */
export const REPORT_REASON_LABELS: Record<string, { km: string; en: string }> = {
  ...Object.fromEntries(
    PROHIBITED_CATEGORIES.map((entry) => [entry.id, entry.title]),
  ),
  not_delivered: {
    km: 'បង់ប្រាក់ហើយ តែមិនបានទទួលទំនិញ',
    en: 'Paid but never received the goods',
  },
  misleading: {
    km: 'ការពិពណ៌នាទំនិញមិនត្រឹមត្រូវ',
    en: 'The listing is misleading',
  },
  other: { km: 'ផ្សេងៗ', en: 'Something else' },
};

/* ------------------------------------------------------------------ */
/* Moderation                                                          */
/* ------------------------------------------------------------------ */

export const MODERATION_ACTIONS = ['warned', 'suspended', 'reinstated', 'closed'] as const;
export type ModerationAction = (typeof MODERATION_ACTIONS)[number];

export const moderateShopSchema = z.object({
  action: z.enum(MODERATION_ACTIONS),
  reason: z.string().min(3).max(500),
  /** The report this action answers, when there is one. */
  reportId: z.string().uuid().nullable().optional(),
});
export type ModerateShopInput = z.infer<typeof moderateShopSchema>;

/* ------------------------------------------------------------------ */
/* Returns-policy templates                                            */
/* ------------------------------------------------------------------ */

/**
 * §6.7: "a returns-policy template shops can adopt".
 *
 * Adopt, not impose — the merchant picks one and it is copied into their own
 * settings, after which it is their text to edit. A shop with no returns policy
 * at all is the single most common source of the disputes that end up as
 * reports, and asking a busy merchant to write one from nothing guarantees the
 * field stays empty.
 */
export interface ReturnPolicyTemplate {
  id: string;
  label: { km: string; en: string };
  /** One line explaining who this suits, shown next to the option. */
  suitedTo: { km: string; en: string };
  body: { km: string; en: string };
}

export const RETURN_POLICY_TEMPLATES: readonly ReturnPolicyTemplate[] = [
  {
    id: 'exchange_only_7d',
    label: { km: 'ប្តូរទំហំក្នុង ៧ ថ្ងៃ', en: 'Size exchange within 7 days' },
    suitedTo: {
      km: 'សម្រាប់ហាងសម្លៀកបំពាក់ភាគច្រើន',
      en: 'What most fashion shops use',
    },
    body: {
      km: [
        'យើងទទួលប្តូរទំហំ ឬពណ៌ ក្នុងរយៈពេល ៧ ថ្ងៃ បន្ទាប់ពីទទួលទំនិញ។',
        '',
        'លក្ខខណ្ឌ៖',
        '• ទំនិញមិនទាន់ប្រើ នៅមានស្លាក និងវេចខ្ចប់ដើម',
        '• មានរូបថត ឬវិក្កយបត្រការបញ្ជាទិញ',
        '• អតិថិជនទទួលបន្ទុកថ្លៃដឹកជញ្ជូនត្រឡប់មកវិញ',
        '',
        'ទំនិញបញ្ចុះតម្លៃពិសេស និងគ្រឿងសម្អាងដែលបានបើកហើយ មិនអាចប្តូរបានទេ។',
      ].join('\n'),
      en: [
        'We accept size or colour exchanges within 7 days of delivery.',
        '',
        'Conditions:',
        '• The item is unworn, with tags and original packaging',
        '• You have a photo or the order receipt',
        '• Return shipping is paid by the customer',
        '',
        'Clearance items and opened cosmetics cannot be exchanged.',
      ].join('\n'),
    },
  },
  {
    id: 'refund_on_fault_7d',
    label: { km: 'សងប្រាក់វិញបើទំនិញខូច', en: 'Refund if the item is faulty' },
    suitedTo: {
      km: 'សម្រាប់ហាងដែលចង់ធានាទំនុកចិត្តខ្ពស់',
      en: 'For shops that want to compete on trust',
    },
    body: {
      km: [
        'បើទំនិញខូច ខុសពីការបញ្ជាទិញ ឬខូចពេលដឹកជញ្ជូន សូមប្រាប់យើងក្នុងរយៈពេល ៧ ថ្ងៃ។',
        '',
        'យើងនឹងជ្រើសរើសជាមួយអ្នក៖ ប្តូរទំនិញថ្មី ឬសងប្រាក់វិញពេញលេញ រួមទាំងថ្លៃដឹកជញ្ជូន។',
        '',
        'សូមផ្ញើរូបថតទំនិញមកមុន ដើម្បីឲ្យយើងដោះស្រាយបានលឿន។',
        '',
        'ការប្តូរដោយសារប្តូរចិត្ត ធ្វើបានក្នុង ៧ ថ្ងៃ ដោយអតិថិជនបង់ថ្លៃដឹក។',
      ].join('\n'),
      en: [
        'If an item arrives faulty, wrong, or damaged in transit, tell us within 7 days.',
        '',
        'We will agree one of two things with you: a replacement, or a full refund including delivery.',
        '',
        'Please send a photo first so we can sort it out quickly.',
        '',
        'Change-of-mind exchanges are also accepted within 7 days, with return shipping paid by the customer.',
      ].join('\n'),
    },
  },
  {
    id: 'final_sale',
    label: { km: 'មិនទទួលប្តូរ ឬសងវិញ', en: 'All sales final' },
    suitedTo: {
      km: 'សម្រាប់ទំនិញកម្មង់ពិសេស ឬលក់បញ្ចុះតម្លៃ',
      en: 'For made-to-order or clearance stock',
    },
    body: {
      km: [
        'ទំនិញដែលបានលក់ហើយ មិនទទួលប្តូរ ឬសងប្រាក់វិញទេ។',
        '',
        'ករណីលើកលែងតែមួយគត់៖ ទំនិញខូច ឬយើងផ្ញើខុស។ សូមប្រាប់យើងក្នុងរយៈពេល ៣ ថ្ងៃ ព្រមទាំងរូបថត។',
        '',
        'សូមពិនិត្យទំហំ និងពណ៌ឲ្យច្បាស់មុនបញ្ជាទិញ។ សួរយើងបានគ្រប់ពេល។',
      ].join('\n'),
      en: [
        'Items are sold as final — no exchanges and no refunds.',
        '',
        'The one exception: the item is faulty, or we sent the wrong thing. Tell us within 3 days with a photo.',
        '',
        'Please check size and colour before ordering. Ask us any time.',
      ].join('\n'),
    },
  },
];

/* ------------------------------------------------------------------ */
/* Tax                                                                 */
/* ------------------------------------------------------------------ */

/**
 * §6.7: "a note pointing merchants to Cambodia e-commerce tax obligations
 * (their responsibility, our heads-up)".
 *
 * Both halves of that matter. It has to be visible enough that a merchant
 * cannot say they were never told, and worded so it is plainly a heads-up
 * rather than advice we are not qualified to give.
 */
export const TAX_NOTICE = {
  title: { km: 'ពន្ធលើការលក់អនឡាញ', en: 'Tax on online sales' },
  body: {
    km: 'ការលក់អនឡាញនៅកម្ពុជាអាចជាប់ពន្ធ។ ការចុះបញ្ជី និងការបង់ពន្ធ គឺជាការទទួលខុសត្រូវរបស់អ្នក មិនមែនរបស់ TeleStore ទេ។ TeleStore មិនផ្តល់ការប្រឹក្សាផ្នែកពន្ធទេ — សូមពិគ្រោះជាមួយអ្នកជំនាញ ឬអគ្គនាយកដ្ឋានពន្ធដារ។ អ្នកអាចនាំចេញការបញ្ជាទិញទាំងអស់ជា CSV ពីទំព័រការកំណត់ ដើម្បីប្រើសម្រាប់គណនេយ្យ។',
    en: 'Selling online in Cambodia may be taxable. Registering and paying is your responsibility, not TeleStore’s. We are not tax advisers — talk to an accountant or the General Department of Taxation. You can export every order as CSV from Settings for your bookkeeping.',
  },
  linkLabel: { km: 'អគ្គនាយកដ្ឋានពន្ធដារ', en: 'General Department of Taxation' },
  url: 'https://www.tax.gov.kh/',
} as const;
